import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tab,
  Tabs,
  Box,
  Typography,
  Avatar,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../../styles/courses.css';
import api from '../../services/api';
import EnrollmentsTab from './components/EnrollmentsTab';

const Modules = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', position: 1 });
  const [currentTab, setCurrentTab] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadCourseAndModules = useCallback(async () => {
    try {
      setLoading(true);
      const courseResponse = await api.get(`/courses/${courseId}`);
      setCourse(courseResponse.data);
      setModules(courseResponse.data.modules || []);
    } catch (err) {
      setError('Erro ao carregar dados do curso');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadCourseAndModules();
  }, [loadCourseAndModules]);

  const handleOpenDialog = (module = null) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        title: module.title,
        description: module.description,
        position: module.position,
      });
    } else {
      setEditingModule(null);
      setFormData({
        title: '',
        description: '',
        position: modules.length + 1,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingModule(null);
    setFormData({ title: '', description: '', position: 1 });
  };

  const handleSubmit = async () => {
    try {
      if (editingModule) {
        await api.put(`/modules/${editingModule.id}`, formData);
      } else {
        await api.post('/modules', { ...formData, courseId });
      }
      handleCloseDialog();
      loadCourseAndModules();
    } catch (err) {
      setError('Erro ao salvar módulo');
      console.error(err);
    }
  };

  const handleDelete = async moduleId => {
    if (window.confirm('Tem certeza que deseja excluir este módulo?')) {
      try {
        await api.delete(`/modules/${moduleId}`);
        loadCourseAndModules();
      } catch (err) {
        setError('Erro ao excluir módulo');
        console.error(err);
      }
    }
  };

  const handleDragEnd = async event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = modules.findIndex(module => module.id === active.id);
      const newIndex = modules.findIndex(module => module.id === over.id);

      const reorderedModules = arrayMove(modules, oldIndex, newIndex);

      // Update positions
      const updatedModules = reorderedModules.map((module, index) => ({
        ...module,
        position: index + 1,
      }));

      setModules(updatedModules);

      // Update backend with all modules at once
      try {
        const payload = {
          courseId,
          modules: updatedModules.map(m => ({ id: m.id, position: m.position })),
        };
        await api.post('/modules/reorder', payload);
        console.log('✅ Módulos reordenados com sucesso');
      } catch (err) {
        console.error('Erro ao atualizar posições dos módulos:', err);
        // Reload to revert
        loadCourseAndModules();
      }
    }
  };

  const handleViewLessons = moduleId => {
    navigate(`/courses/${courseId}/modules/${moduleId}`);
  };

  const SortableItem = ({ module, onViewLessons, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: module.id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <div
          className="drag-handle"
          {...attributes}
          {...listeners}
          style={{ cursor: 'grab', padding: '8px', background: '#f0f0f0', marginBottom: '8px' }}
        >
          ⋮⋮ Arraste para reordenar
        </div>
        <div className="management-card">
          <div className="card-header">
            <div className="card-icon">📚</div>
            <div className="card-header-content">
              <h3 className="card-title">{module.title}</h3>
              <p className="card-position">Posição: {module.position}</p>
            </div>
          </div>

          <div className="card-body">
            <p className="card-description">{module.description || 'Sem descrição'}</p>
            <div className="card-meta">
              <span className="meta-item">
                <span className="meta-label">Aulas:</span>
                <span className="meta-value">{module.lessons?.length || 0}</span>
              </span>
            </div>
          </div>

          <div className="card-actions">
            <button
              className="action-button primary"
              onClick={() => onViewLessons(module.id)}
              title="Ver aulas deste módulo"
            >
              ▶ Ver Aulas
            </button>
            <button
              className="action-button secondary"
              onClick={() => onEdit(module)}
              title="Editar este módulo"
            >
              ✏ Editar
            </button>
            <button
              className="action-button danger"
              onClick={() => onDelete(module.id)}
              title="Deletar este módulo"
            >
              🗑 Deletar
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="modules-page">
        <div className="loading-container">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modules-page">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="modules-page">
      {/* Header with back button */}
      <header className="management-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate('/courses')}
            title="Voltar para Cursos"
          >
            ← Voltar
          </button>
          <div className="header-title-section">
            <h1>{course?.title}</h1>
            <p className="header-subtitle">Gerenciar Módulos</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="add-button-fab"
            onClick={() => handleOpenDialog()}
            title="Adicionar novo módulo"
          >
            +
          </button>
        </div>
      </header>

      {/* Breadcrumb navigation */}
      <nav className="breadcrumb-nav">
        <span className="breadcrumb-item" onClick={() => navigate('/courses')}>
          Cursos
        </span>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item active">{course?.title}</span>
      </nav>

      {/* Error message if exists */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content area with Tabs */}
      <Card
        sx={{
          backgroundColor: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(59, 130, 246, 0.1)',
          borderRadius: '12px',
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
            backgroundColor: 'rgba(30, 41, 59, 0.4)',
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              '&.Mui-selected': {
                color: '#3b82f6',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          <Tab label="📚 Módulos" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="👥 Matrículas" id="tab-1" aria-controls="tabpanel-1" />
        </Tabs>

        {/* TAB 1: MÓDULOS */}
        <CardContent sx={{ backgroundColor: 'rgba(30, 41, 59, 0.4)' }}>
          {currentTab === 0 && (
            <div className="management-content">
              {modules.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📚</div>
                  <h2>Nenhum módulo criado</h2>
                  <p>Crie seu primeiro módulo clicando no botão acima</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={modules.map(m => m.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="content-grid">
                      {modules.map(module => (
                        <SortableItem
                          key={module.id}
                          module={module}
                          onViewLessons={handleViewLessons}
                          onEdit={handleOpenDialog}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}

          {/* TAB 2: MATRÍCULAS */}
          {currentTab === 1 && <EnrollmentsTab courseId={courseId} />}
        </CardContent>
      </Card>

      {/* Dialog for creating/editing modules */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            fontWeight: 600,
            fontSize: '1.25rem',
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <MenuBookIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              {editingModule ? 'Editar Módulo' : 'Novo Módulo'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2.5,
            borderTop: '1px solid rgba(59, 130, 246, 0.1)',
            minHeight: '60vh',
          }}
        >
          {/* Título */}
          <TextField
            autoFocus
            sx={{
              width: '100%',
              maxWidth: 500,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                borderRadius: '12px',
                '& input': { color: '#ffffff' },
                '&:hover fieldset': { borderColor: '#3b82f6' },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#3b82f6',
              },
            }}
            label="Título do Módulo"
            placeholder="Digite o título do módulo"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            variant="outlined"
            required
          />

          {/* Descrição */}
          <TextField
            sx={{
              width: '100%',
              maxWidth: 500,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                borderRadius: '12px',
                '& textarea': { color: '#ffffff' },
                '&:hover fieldset': { borderColor: '#3b82f6' },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#3b82f6',
              },
            }}
            label="Descrição"
            placeholder="Descreva o conteúdo deste módulo..."
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            variant="outlined"
          />

          {/* Posição */}
          <TextField
            sx={{
              width: '100%',
              maxWidth: 500,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                borderRadius: '12px',
                '& input': { color: '#ffffff' },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
            label="Posição"
            type="number"
            value={formData.position}
            onChange={e => setFormData({ ...formData, position: parseInt(e.target.value) || 1 })}
            disabled
            helperText="A posição é reordenada automaticamente"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            borderTop: '1px solid rgba(59, 130, 246, 0.1)',
            p: 2.5,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderColor: 'rgba(59, 130, 246, 0.5)',
              color: '#3b82f6',
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 600,
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              borderRadius: '12px',
              px: 4,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
              },
              '&:disabled': {
                background: '#475569',
                color: '#94a3b8',
              },
            }}
          >
            {editingModule ? 'Salvar Módulo' : 'Criar Módulo'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Modules;
